import React, { useEffect } from 'react';
import { UserMessageBubble as OriginalUserMessageBubble, AssistantMessageBubble as OriginalAssistantMessageBubble } from './MessageBubble';
import WelcomeScreen from './WelcomeScreen';
import { motion } from 'framer-motion';

// Animation variants for messages
const messageAnimations = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 }
};

// HOC to add animation to the UserMessageBubble
const UserMessageBubble = (props) => {
  return (
    <motion.div
      initial="initial"
      animate="animate"
      variants={messageAnimations}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >
      <OriginalUserMessageBubble {...props} />
    </motion.div>
  );
};

// HOC to add animation to the AssistantMessageBubble
const AssistantMessageBubble = (props) => {
  return (
    <motion.div
      initial="initial"
      animate="animate"
      variants={messageAnimations}
      transition={{ duration: 0.4, ease: "easeOut", delay: 0.2 }}
    >
      <OriginalAssistantMessageBubble {...props} />
    </motion.div>
  );
};

/**
 * Conversation container component that displays all messages with animations
 * @param {Object} props - Same props as original ConversationContainer
 * @returns {JSX.Element} The animated conversation container component
 */
const AnimatedConversationContainer = ({
  responses,
  loadingIndex,
  isOnPhone,
  currentUser,
  detectedGoLinks,
  conversationId,
  feedbackSubmitted,
  submittingFeedback,
  handleFeedback,
  conversationContainerRef,
  latestMessageRef,
  scrollToLatestMessage
}) => {
  // Scroll to the latest message when responses or loading state changes
  useEffect(() => {
    if (responses.length > 0 && loadingIndex === null) {
      // Scroll to the latest message when loading finishes
      setTimeout(() => {
        scrollToLatestMessage();
      }, 500); // Slightly longer delay to allow animations to complete
    }
  }, [responses, loadingIndex, scrollToLatestMessage]);

  return (
    <div
      className="flex-1 w-full max-w-3xl mx-auto overflow-y-auto space-y-8 pt-16 pb-24"
      ref={conversationContainerRef}
    >
      {/* Welcome message when there are no responses */}
      {responses.length === 0 ? (
        <WelcomeScreen isOnPhone={isOnPhone} />
      ) : (
        /* Render conversation with animated components */
        responses.map((item, index) => (
          <div key={index} className={`flex flex-col space-y-6 ${isOnPhone && index === 0 && "mt-2"}`}>
            {/* Query from user */}
            <UserMessageBubble
              message={item.query}
              isLatest={index === responses.length - 1}
              latestRef={latestMessageRef}
              photoURL={currentUser?.photoURL}
              displayName={currentUser?.displayName}
            />

            {/* Response from assistant */}
            <AssistantMessageBubble
              message={item.response}
              isLoading={loadingIndex === index}
              isLatest={index === responses.length - 1}
              detectedGoLinks={index === responses.length - 1 ? detectedGoLinks : []}
              conversationId={conversationId}
              feedbackSubmitted={index === responses.length - 1 ? feedbackSubmitted : null}
              submittingFeedback={submittingFeedback}
              handleFeedback={handleFeedback}
            />
          </div>
        ))
      )}
    </div>
  );
};

export default AnimatedConversationContainer;